/**
 * Unit localization (REQ-006).
 *
 * Storage is always SI/metric — kg, cm, °C, mL, g, km — as FHIR/UCUM carries
 * it. Conversion happens only at the edge: when a value is shown, and when a
 * value typed by the user is turned back into the stored unit.
 *
 * The display system follows the app language: English uses US customary
 * units (lb, ft/in, °F, fl oz, oz, mi), every other language metric. This
 * supersedes the "en = metric, EU default" line in REQ-006 BR-2 by the
 * owner's decision of 2026-10-08.
 */

import type { Language } from "../types/i18n";
import { formatNumber } from "./dateLocalization";

export type UnitSystem = "metric" | "imperial";

export function getUnitSystem(language: Language): UnitSystem {
  return language === "en" ? "imperial" : "metric";
}

/**
 * An explicit choice per quantity, which wins over the language. Supplied by
 * the hooks from the unit preferences store, so a user who chose kg sees kg on
 * every screen, not only in the inputs (`systemsOf` there builds it).
 */
export type QuantitySystems = Partial<Record<Quantity, UnitSystem>>;

function systemFor(
  quantity: Quantity,
  language: Language,
  systems?: QuantitySystems,
): UnitSystem {
  return systems?.[quantity] ?? getUnitSystem(language);
}

// ---------------------------------------------------------------------------
// Conversions (exact factors)
// ---------------------------------------------------------------------------

const LB_PER_KG = 1 / 0.45359237;
const CM_PER_IN = 2.54;
const ML_PER_FL_OZ = 29.5735295625;
const G_PER_OZ = 28.349523125;
const KM_PER_MI = 1.609344;

export const kgToLb = (kg: number): number => kg * LB_PER_KG;
export const lbToKg = (lb: number): number => lb / LB_PER_KG;
export const cmToIn = (cm: number): number => cm / CM_PER_IN;
export const inToCm = (inches: number): number => inches * CM_PER_IN;
export const cToF = (c: number): number => (c * 9) / 5 + 32;
export const fToC = (f: number): number => ((f - 32) * 5) / 9;
export const mlToFlOz = (ml: number): number => ml / ML_PER_FL_OZ;
export const flOzToMl = (flOz: number): number => flOz * ML_PER_FL_OZ;
export const gToOz = (g: number): number => g / G_PER_OZ;
export const ozToG = (oz: number): number => oz * G_PER_OZ;
export const kmToMi = (km: number): number => km / KM_PER_MI;
export const miToKm = (mi: number): number => mi * KM_PER_MI;

/** 175 cm → { feet: 5, inches: 9 }; inches are rounded, never 12. */
export function cmToFeetInches(cm: number): { feet: number; inches: number } {
  const total = Math.round(cmToIn(cm));
  return { feet: Math.floor(total / 12), inches: total % 12 };
}

export function feetInchesToCm(feet: number, inches: number): number {
  return inToCm(feet * 12 + inches);
}

// ---------------------------------------------------------------------------
// Quantities: one descriptor per kind, so display and input share the same
// unit label and conversion.
// ---------------------------------------------------------------------------

export type Quantity =
  | "weight" // stored kg
  | "height" // stored cm
  | "temperature" // stored °C
  | "volume" // stored mL
  | "mass" // stored g (food, small amounts)
  | "distance"; // stored km

interface QuantitySpec {
  metric: { unit: string; decimals: number };
  imperial: {
    unit: string;
    decimals: number;
    fromStored: (v: number) => number;
    toStored: (v: number) => number;
  };
}

const QUANTITIES: Record<Quantity, QuantitySpec> = {
  weight: {
    metric: { unit: "kg", decimals: 1 },
    imperial: { unit: "lb", decimals: 1, fromStored: kgToLb, toStored: lbToKg },
  },
  height: {
    metric: { unit: "cm", decimals: 0 },
    imperial: { unit: "in", decimals: 0, fromStored: cmToIn, toStored: inToCm },
  },
  temperature: {
    metric: { unit: "°C", decimals: 1 },
    imperial: { unit: "°F", decimals: 1, fromStored: cToF, toStored: fToC },
  },
  volume: {
    metric: { unit: "mL", decimals: 0 },
    imperial: {
      unit: "fl oz",
      decimals: 1,
      fromStored: mlToFlOz,
      toStored: flOzToMl,
    },
  },
  mass: {
    metric: { unit: "g", decimals: 0 },
    imperial: { unit: "oz", decimals: 1, fromStored: gToOz, toStored: ozToG },
  },
  distance: {
    metric: { unit: "km", decimals: 1 },
    imperial: { unit: "mi", decimals: 1, fromStored: kmToMi, toStored: miToKm },
  },
};

/** The unit label shown next to a value or an input: "lb" / "kg". */
export function unitLabel(
  quantity: Quantity,
  language: Language,
  systems?: QuantitySystems,
): string {
  const spec = QUANTITIES[quantity];
  return systemFor(quantity, language, systems) === "imperial"
    ? spec.imperial.unit
    : spec.metric.unit;
}

/** Stored value → number in the display unit, rounded for display/input. */
export function toDisplayValue(
  quantity: Quantity,
  stored: number,
  language: Language,
  systems?: QuantitySystems,
): number {
  const spec = QUANTITIES[quantity];
  const imperial = systemFor(quantity, language, systems) === "imperial";
  const value = imperial ? spec.imperial.fromStored(stored) : stored;
  const decimals = imperial ? spec.imperial.decimals : spec.metric.decimals;
  const f = 10 ** decimals;
  return Math.round(value * f) / f;
}

/** A number the user typed in the display unit → the stored SI value. */
export function fromDisplayValue(
  quantity: Quantity,
  entered: number,
  language: Language,
  systems?: QuantitySystems,
): number {
  const spec = QUANTITIES[quantity];
  return systemFor(quantity, language, systems) === "imperial"
    ? spec.imperial.toStored(entered)
    : entered;
}

/**
 * Format a stored value with its unit: formatQuantity("weight", 70, "en") →
 * "154.3 lb", (…, "de") → "70 kg". Height in English is "5 ft 9 in".
 * Returns "–" for a missing value.
 */
export function formatQuantity(
  quantity: Quantity,
  stored: number | null | undefined,
  language: Language,
  systems?: QuantitySystems,
): string {
  if (stored === null || stored === undefined || Number.isNaN(stored)) {
    return "–";
  }
  const imperial = systemFor(quantity, language, systems) === "imperial";
  if (quantity === "height" && imperial) {
    const { feet, inches } = cmToFeetInches(stored);
    return `${feet} ft ${inches} in`;
  }
  const spec = QUANTITIES[quantity];
  const decimals = imperial ? spec.imperial.decimals : spec.metric.decimals;
  const value = toDisplayValue(quantity, stored, language, systems);
  const sep = quantity === "temperature" ? "" : " ";
  return `${formatNumber(value, language, decimals)}${sep}${unitLabel(quantity, language, systems)}`;
}

export const formatWeight = (
  kg: number | null | undefined,
  l: Language,
  systems?: QuantitySystems,
) => formatQuantity("weight", kg, l, systems);
export const formatHeight = (
  cm: number | null | undefined,
  l: Language,
  systems?: QuantitySystems,
) => formatQuantity("height", cm, l, systems);
export const formatTemperature = (
  c: number | null | undefined,
  l: Language,
  systems?: QuantitySystems,
) => formatQuantity("temperature", c, l, systems);
export const formatVolume = (ml: number | null | undefined, l: Language) =>
  formatQuantity("volume", ml, l);
export const formatDistance = (km: number | null | undefined, l: Language) =>
  formatQuantity("distance", km, l);
